import type {
  IAnalyticsMessage,
  IAnalyticsMessagesParams,
  IAnalyticsQueryParams,
  IAnalyticsResponse,
  IAnalyticsScopeParams,
  IChatThreadWindowParams,
  IChatThreadWindowResponse,
  IDownloadAnalyticsMessageFileParams,
  IMessageContextParams,
  IMessageFileAnonymization,
  IMessageFileAnonymizationParams,
  IMessageModelAndProviderOptionsResponse,
  IMessagesSummary,
  IPaginatedAnalyticsMessagesResponse,
  IPreviewAnalyticsMessageFileParams,
  IRecentFlaggedMessagesParams,
  TMessageContextResponse,
} from "../types/";
import type { ApiClient } from "./client";

export class AnalyticsApi {
  constructor(private apiClient: ApiClient) {}

  async getUserAnalytics(
    params: IAnalyticsQueryParams,
  ): Promise<IAnalyticsResponse> {
    const {
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    // NOTE: The personal /analytics/dashboard DTO doesn't whitelist includeDeleted;
    // only forward it on the all-members route to avoid forbidNonWhitelisted 400s.
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/dashboard/all-members`
      : `/analytics/dashboard`;
    const response = await this.apiClient.get<IAnalyticsResponse>(
      `${endpoint}?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessagesSummary(
    params: IAnalyticsQueryParams,
  ): Promise<IMessagesSummary> {
    const {
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/summary/all-members`
      : `/analytics/summary`;
    const query = searchParams.toString();
    const response = await this.apiClient.get<IMessagesSummary>(
      `${endpoint}${query ? `?${query}` : ""}`,
    );
    return response.data;
  }

  async getRecentFlaggedMessages(
    params: IRecentFlaggedMessagesParams,
  ): Promise<IAnalyticsMessage[]> {
    const {
      limit,
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (limit) searchParams.append("limit", limit.toString());
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/recent-flagged/all-members`
      : `/analytics/recent-flagged`;
    const response = await this.apiClient.get<IAnalyticsMessage[]>(
      `${endpoint}?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessageContext(
    params: IMessageContextParams,
  ): Promise<TMessageContextResponse> {
    const {
      chatUuid,
      messageUuid,
      projectUuid,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("chatUuid", chatUuid);
    searchParams.append("messageUuid", messageUuid);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    if (shouldIncludeDeleted) searchParams.append("includeDeleted", "true");
    const response = await this.apiClient.get<TMessageContextResponse>(
      `/analytics/message-context?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getChatThreadWindow(
    params: IChatThreadWindowParams,
  ): Promise<IChatThreadWindowResponse> {
    const {
      chatUuid,
      anchorMessageUuid,
      projectUuid,
      limit,
      direction,
      cursorCreatedAt,
      cursorUuid,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("chatUuid", chatUuid);
    searchParams.append("anchorMessageUuid", anchorMessageUuid);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    if (limit != null) searchParams.append("limit", String(limit));
    if (direction) searchParams.append("direction", direction);
    if (cursorCreatedAt)
      searchParams.append("cursorCreatedAt", cursorCreatedAt);
    if (cursorUuid) searchParams.append("cursorUuid", cursorUuid);
    if (shouldIncludeDeleted) searchParams.append("includeDeleted", "true");
    const response = await this.apiClient.get<IChatThreadWindowResponse>(
      `/analytics/chat-thread-window?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessages(
    params: IAnalyticsMessagesParams,
  ): Promise<IPaginatedAnalyticsMessagesResponse> {
    const {
      hasAllMembersAnalyticsReadPermission,
      messageUuid,
      limit,
      offset,
      sortColumn,
      sortOrder,
      memberUuids,
      direction,
      source,
      status,
      modelTitle,
      providerName,
      assistantName,
      dateFrom,
      dateTo,
      projectUuid,
      scope,
      deletionStatus,
      search,
    } = params;

    const searchParams = new URLSearchParams();
    const appendList = ({
      key,
      values,
    }: {
      key: string;
      values: string[] | undefined;
    }): void => {
      values?.forEach((value) => searchParams.append(key, value));
    };

    if (messageUuid) searchParams.append("messageUuid", messageUuid);
    if (limit !== undefined) searchParams.append("limit", limit.toString());
    if (offset !== undefined) searchParams.append("offset", offset.toString());
    if (sortColumn) searchParams.append("sortColumn", sortColumn);
    if (sortOrder) searchParams.append("sortOrder", sortOrder);
    if (hasAllMembersAnalyticsReadPermission) {
      appendList({ key: "memberUuid", values: memberUuids });
    }
    if (direction) searchParams.append("direction", direction);
    appendList({ key: "source", values: source });
    appendList({ key: "status", values: status });
    appendList({ key: "modelTitle", values: modelTitle });
    appendList({ key: "providerName", values: providerName });
    appendList({ key: "assistantName", values: assistantName });
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    if (scope) searchParams.append("scope", scope);
    if (deletionStatus) {
      searchParams.append("deletionStatus", deletionStatus);
    }
    if (search) searchParams.append("search", search);

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/messages/all-members`
      : `/analytics/messages`;
    const query = searchParams.toString();
    const response =
      await this.apiClient.get<IPaginatedAnalyticsMessagesResponse>(
        `${endpoint}${query ? `?${query}` : ""}`,
      );
    return response.data;
  }

  async downloadMessageFile(
    params: IDownloadAnalyticsMessageFileParams,
  ): Promise<Blob> {
    const { fileUuid, projectUuid, scope, onProgress } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<Blob>(
      `/analytics/message-files/${fileUuid}/download${query ? `?${query}` : ""}`,
      {
        responseType: "blob",
        onDownloadProgress: onProgress
          ? (event) =>
              onProgress({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
      },
    );

    return response.data;
  }

  async previewMessageFile(
    params: IPreviewAnalyticsMessageFileParams,
  ): Promise<Blob> {
    const { fileUuid, projectUuid, scope } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<Blob>(
      `/analytics/message-files/${fileUuid}/preview${query ? `?${query}` : ""}`,
      {
        responseType: "blob",
      },
    );

    return response.data;
  }

  // NOTE: NSAI-6839 — how an uploaded chat attachment was anonymized. Same
  // scope params as the download / preview routes; the server also admits an
  // ANALYTICS_READ-only caller, narrowed to their own files.
  async getMessageFileAnonymization(
    params: IMessageFileAnonymizationParams,
  ): Promise<IMessageFileAnonymization> {
    const { fileUuid, projectUuid, scope } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<IMessageFileAnonymization>(
      `/analytics/message-files/${fileUuid}/anonymization${query ? `?${query}` : ""}`,
    );

    return response.data;
  }

  async getMessageModelAndProviderOptions(
    params: {
      hasAllMembersAnalyticsReadPermission: boolean;
      memberUuid?: string;
      includeDeleted?: boolean;
    } & IAnalyticsScopeParams,
  ): Promise<IMessageModelAndProviderOptionsResponse> {
    const endpoint = params.hasAllMembersAnalyticsReadPermission
      ? `/analytics/messages/all-members/filter-options`
      : `/analytics/messages/filter-options`;
    const searchParams = new URLSearchParams();
    if (params.hasAllMembersAnalyticsReadPermission && params.memberUuid) {
      searchParams.set("memberUuid", params.memberUuid);
    }
    if (params.projectUuid) {
      searchParams.set("projectUuid", params.projectUuid);
    }
    if (params.scope) searchParams.set("scope", params.scope);
    if (params.hasAllMembersAnalyticsReadPermission && params.includeDeleted) {
      searchParams.set("includeDeleted", "true");
    }
    const query = searchParams.toString();
    const response =
      await this.apiClient.get<IMessageModelAndProviderOptionsResponse>(
        `${endpoint}${query ? `?${query}` : ""}`,
      );
    return response.data;
  }
}
